import { useMemo } from 'react';

import { ProgressIndicatorItem } from '.';

export interface ProgressIndicatorItemSingleModified {
  type: 'single';
  active: boolean;
  passed: boolean;
  last: boolean;
  index: number;
  key: string;
}

export interface ProgressIndicatorItemMultiModified {
  type: 'multi';
  active: boolean;
  passed: boolean;
  last: boolean;
  substeps: ProgressIndicatorItemSingleModified[];
  index: number;
  key: string;
}

export type ProgressIndicatorItemModified =
  | ProgressIndicatorItemSingleModified
  | ProgressIndicatorItemMultiModified;

export interface ProgressIndicatorHook {
  modifiedItems: ProgressIndicatorItemModified[];
}

export default function useProgressIndicator(
  items: ProgressIndicatorItem[],
  lastPassed = false,
): ProgressIndicatorHook {
  const activeIndex = useMemo(() => {
    if (items.length === 0) return -1;
    const isActive = items.map((i, index) => {
      if (i.type === 'single') {
        const { active } = i;
        return active ? index : -1;
      }
      const { substeps } = i;
      if (!substeps) return index;
      return substeps.some((si) => si.active) ? index : 0 - 1;
    });
    const maxIndex = Math.max(...isActive);
    const finalIsMulti = items[items.length - 1].type === 'multi';
    if (maxIndex === items.length - 1 && lastPassed && !finalIsMulti)
      return items.length;
    return maxIndex;
  }, [items, lastPassed]);

  const modifiedItems: ProgressIndicatorItemModified[] = useMemo(
    () =>
      items.map((item, index) => {
        const { type } = item;
        const active = index === activeIndex;
        const passed = index < activeIndex;
        if (type === 'single') {
          const a: ProgressIndicatorItemSingleModified = {
            type,
            active,
            passed,
            last: index === items.length - 1,
            index,
            key: `i-${index}`,
          };
          return a;
        }
        const { substeps } = item;
        const subMaxActiveIndex = Math.max(
          ...substeps.map((i, idx) => (i.active ? idx : -1)),
        );
        const subActiveIndex =
          subMaxActiveIndex === substeps.length - 1 &&
          index === items.length - 1 &&
          lastPassed
            ? subMaxActiveIndex + 1
            : subMaxActiveIndex;
        const b: ProgressIndicatorItemMultiModified = {
          type,
          active,
          passed,
          index,
          last: index === items.length - 1,
          key: `i-${index}`,
          substeps: substeps.map((_, subIndex) => ({
            type: 'single',
            active: subIndex === subActiveIndex,
            passed: subIndex < subActiveIndex,
            last:
              index === items.length - 1 && subIndex === substeps.length - 1,
            index: subIndex,
            key: `i-${index}-${subIndex}`,
          })),
        };
        return b;
      }),
    [items, lastPassed, activeIndex],
  );

  return {
    modifiedItems,
  };
}
